import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CancelConfirmationActionSheet } from '@/components/order-guide/cancel-confirmation-action-sheet';
import { EDIT_MULTI_ORDER_GUIDE_QUERY } from '@/contexts/items-context-queries';
import { EDIT_ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import {
  EditOrderGuideActions,
  Item,
  Partition,
} from '@/hooks/use-edit-order-guide-reducer';
import { EditOrderGuideStackParams } from '@/navigators/types';
import {
  ItemsContext_EditMultiOrderGuideItemsQuery,
  ItemsContext_EditMultiOrderGuideItemsQueryVariables,
} from '@/types';
import { OrderGuideEditor } from '@/components/order-guide/order-guide-editor';
import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useBlockNavigation, VoidFunction } from '@/hooks/use-block-navigation';
import { useEditOrderGuideActions } from '@/contexts/edit-order-guide-context';
import { useOrderGuideItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export interface Props {
  navigation: StackNavigationProp<
    EditOrderGuideStackParams,
    EDIT_ORDER_GUIDE_ROUTES.Base
  >;
  route: RouteProp<EditOrderGuideStackParams, EDIT_ORDER_GUIDE_ROUTES.Base>;
}

export const EditOrderGuideScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const dispatch = useEditOrderGuideActions();
  const itemQueryVariables = useOrderGuideItemQueryVariables();
  const { selectedRestaurantRelationship, selectedRestaurantUUID } =
    useUserContext();
  const { showErrorToast } = useToasts();

  const [cancelConfirmSheetOpen, setCancelConfirmSheetOpen] = useState(false);
  const stateHydratedRef = useRef(false);
  const deletedRef = useRef(false);
  const confirmNavRef = useRef<VoidFunction | null>(null);

  const {
    collectionUUID,
    initialSelectedPartitionUUID,
    setSelectedCollectionUUID,
  } = route.params;

  const { data, error, hasResponse, loading } = useWrappedQuery<
    ItemsContext_EditMultiOrderGuideItemsQuery,
    ItemsContext_EditMultiOrderGuideItemsQueryVariables
  >(EDIT_MULTI_ORDER_GUIDE_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      ...itemQueryVariables,
      collectionUUID,
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    },
  });

  const unblockNavigation = useBlockNavigation(
    i18n.t('edit_cancel_modal.title'),
    (cb: () => void) => {
      confirmNavRef.current = cb;
      setCancelConfirmSheetOpen(true);
    },
  );

  const navigateBack = useCallback(() => {
    if (confirmNavRef.current) {
      confirmNavRef.current();
    } else {
      unblockNavigation();
      navigation.goBack();
    }
  }, [unblockNavigation, navigation]);

  const [foundCollection, foundPartitions, deleteCollectionMode] =
    useMemo(() => {
      // First try to find a restaurant collection that matches the collection UUID
      const foundRestaurantCollection =
        data?.order_guide_restaurant_collection.find(
          (collection) =>
            collection.uuid === collectionUUID &&
            collection.order_guide_restaurant_partitions.length > 0,
        );

      if (foundRestaurantCollection) {
        return [
          foundRestaurantCollection,
          foundRestaurantCollection.order_guide_restaurant_partitions,
          foundRestaurantCollection.linked_order_guide_supplier_collection_uuid
            ? 'reset'
            : 'delete',
        ] as const;
      }

      // Next try to find a supplier collection that matches the collection UUID
      const foundSupplierCollection =
        data?.order_guide_supplier_collection.find(
          (collection) =>
            collection.uuid === collectionUUID &&
            collection.order_guide_supplier_partitions.length > 0,
        );

      if (foundSupplierCollection) {
        return [
          foundSupplierCollection,
          foundSupplierCollection.order_guide_supplier_partitions,
          null,
        ] as const;
      }

      return [null, [], null] as const;
    }, [collectionUUID, data]);

  useEffect(() => {
    // If the collection is not found, show an error and navigate back
    let hasValidCollection = !hasResponse || foundCollection;
    if (!deletedRef.current && (error || !hasValidCollection)) {
      showErrorToast();
      navigateBack();
    }
  }, [data, error, foundCollection, hasResponse, navigateBack, showErrorToast]);

  useEffect(() => {
    // context may refresh on screen focus. Prevent overwriting state
    // by only hydrating once
    if (stateHydratedRef.current) {
      return;
    }

    if (data && foundCollection) {
      // Create a map of linked supplier -> restaurant partitions so items from
      // the linked supplier collection are included in the list
      let supplierToRestaurantPartitionMap: Record<string, string> = {};
      if (
        'linked_order_guide_supplier_collection_uuid' in foundCollection &&
        foundCollection.linked_order_guide_supplier_collection_uuid
      ) {
        const defaultRestaurantPartitionUUID = foundPartitions.find(
          (p) => p.is_default,
        )?.uuid;
        const defaultSupplierPartitionUUID =
          data.order_guide_supplier_collection
            .find(
              (ogsc) =>
                ogsc.uuid ===
                foundCollection.linked_order_guide_supplier_collection_uuid,
            )
            ?.order_guide_supplier_partitions.find((p) => p.is_default)?.uuid;
        if (defaultRestaurantPartitionUUID && defaultSupplierPartitionUUID) {
          supplierToRestaurantPartitionMap[defaultSupplierPartitionUUID] =
            defaultRestaurantPartitionUUID;
        }
      }

      const partitionItemsMap = data.getOrderGuideItems.reduce<{
        [partitionUUID: string]: Item[];
      }>((acc, ogi) => {
        const orderGuideRestaurant = ogi.order_guide_restaurant;
        const item: Item = {
          isMinimized: orderGuideRestaurant?.is_minimized ?? false,
          itemListOption: adaptVariantPackToItemListOption(ogi.variant_pack!),
          itemUUID: ogi.order_guide_item_id,
          notes: orderGuideRestaurant?.notes || null,
          parLevel: orderGuideRestaurant?.par_level || null,
          persistNotes: orderGuideRestaurant?.persist_notes || false,
        };

        let partitionUUID = ogi.order_guide_item_partition_id;
        if (partitionUUID) {
          partitionUUID =
            supplierToRestaurantPartitionMap[partitionUUID] || partitionUUID;

          if (!acc[partitionUUID]) {
            acc[partitionUUID] = [];
          }

          acc[partitionUUID].push(item);
        }

        return acc;
      }, {});

      let selectedPartitionUUID: string | null = null;
      const partitions = foundPartitions.map<Partition>((partition) => {
        if (partition.uuid === initialSelectedPartitionUUID) {
          selectedPartitionUUID = initialSelectedPartitionUUID;
        }

        const items = partitionItemsMap[partition.uuid] || [];
        return {
          isDefault: partition.is_default,
          items,
          partitionName: partition.name || null,
          partitionUUID: partition.uuid,
        };
      });

      stateHydratedRef.current = true;
      dispatch({
        payload: {
          hydratedState: {
            collectionName: foundCollection.name || null,
            collectionUUID,
            partitions,
            selectedPartitionUUID:
              selectedPartitionUUID || partitions[0]?.partitionUUID || null,
          },
        },
        type: EditOrderGuideActions.HYDRATE,
      });
    }
  }, [
    adaptVariantPackToItemListOption,
    collectionUUID,
    data,
    dispatch,
    foundCollection,
    foundPartitions,
    initialSelectedPartitionUUID,
  ]);

  const [collectionNames, canUpdateCollection] = useMemo(() => {
    let selectedCollectionEditable = false;
    const names = (data?.order_guide_restaurant_collection || []).reduce<
      string[]
    >((acc, collection) => {
      if (!collection.name || collection.is_default) {
        selectedCollectionEditable = collection.uuid !== collectionUUID;
      } else if (collection.uuid !== collectionUUID) {
        acc.push(collection.name);
      }
      return acc;
    }, []);

    return [names, selectedCollectionEditable];
  }, [data, collectionUUID]);

  return (
    <>
      <OrderGuideEditor
        canUpdateCollection={canUpdateCollection}
        collectionNames={collectionNames}
        collectionUUID={collectionUUID}
        deleteCollectionMode={deleteCollectionMode}
        loading={loading}
        onCancelButtonPress={() => setCancelConfirmSheetOpen(true)}
        onDelete={() => {
          if (
            deleteCollectionMode === 'reset' &&
            foundCollection.linked_order_guide_supplier_collection_uuid
          ) {
            // After resetting an OGRC, select the linked OGSC instead of
            // reverting to the default collection
            setSelectedCollectionUUID(
              foundCollection.linked_order_guide_supplier_collection_uuid,
            );
          }

          deletedRef.current = true;
          navigateBack();
        }}
        onSaved={navigateBack}
      />
      {cancelConfirmSheetOpen ? (
        <CancelConfirmationActionSheet
          onCancel={() => setCancelConfirmSheetOpen(false)}
          onConfirm={() => {
            navigateBack();
            setCancelConfirmSheetOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
